import { getPeriodKey, isNLevelClassReport } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ColumnGroupConfig } from "@lib/views";

interface ClassColumnGrouping {
    groups: Record<string, ColumnGroupConfig>;
    groupPathByColumnId: Record<string, string[]>;
    // The header tier each column's label should read at: its bracket-ancestor
    // depth. Leaf classes and a parent's own Direct/Total share this depth with
    // the parent's immediate children, so Direct/Total sit on the same tier row
    // as the sibling subclasses they break down — not the global bottom row.
    headerTierDepthByColumnId: Record<string, number>;
}

// Returns undefined for non-class reports so their columns render unchanged.
export function buildClassColumnGrouping(
    periods: ReportPeriodWithLabel[],
): ClassColumnGrouping | undefined {
    if (!isNLevelClassReport(periods)) {
        return undefined;
    }

    const bracketNodeIds = new Set<string>();
    periods.forEach((period) => {
        const isBracketOwner =
            period.classColumnRole === "parentDirect" ||
            period.classColumnRole === "parentTotal";
        const ownNode = period.classHeaderPath?.at(-1);
        if (isBracketOwner && ownNode) {
            bracketNodeIds.add(ownNode.classId);
        }
    });

    const groups: Record<string, ColumnGroupConfig> = {};
    const groupPathByColumnId: Record<string, string[]> = {};
    const headerTierDepthByColumnId: Record<string, number> = {};

    periods.forEach((period) => {
        if (period.isTotal) {
            return;
        }
        const columnId = `period-${getPeriodKey(period)}`;
        const bracketEntries = (period.classHeaderPath ?? []).filter((entry) =>
            bracketNodeIds.has(entry.classId),
        );
        const groupPath = bracketEntries.map((entry) => entry.classId);
        groupPathByColumnId[columnId] = groupPath;
        headerTierDepthByColumnId[columnId] = groupPath.length;
        bracketEntries.forEach((entry) => {
            groups[entry.classId] = { header: entry.label };
        });
    });

    return { groups, groupPathByColumnId, headerTierDepthByColumnId };
}
